import React, { useState, useEffect } from 'react';
import {
  X,
  Sparkles,
  User,
  Clock,
  CheckCircle2,
  AlertTriangle,
  Play,
  ArrowRight,
  FileText,
  FileSignature,
  Mail,
  Calendar,
  CreditCard,
  FileSpreadsheet,
  Ticket,
  CheckSquare,
  Tag,
  GitCommit,
  FolderPlus,
  ShieldCheck,
  RefreshCw,
} from 'lucide-react';
import { AutomationActionType, AutomationBatchSource, AutomationDefinition } from '../../../types/automation';
import { computeNodeSequenceMap } from '../automation/automationChain';
import SearchableSelect from '../../shared/SearchableSelect';
import { safeFetch } from '../../../lib/api';

interface AssignAutomationModalProps {
  isOpen: boolean;
  onClose: () => void;
  referenceApplicant?: {
    id: string;
    applicantName?: string;
    fullName?: string;
    applicationId?: string;
    customApplicationId?: string;
    program?: string;
    programName?: string;
    stage?: string;
    currentStage?: string;
  } | null;
  applicantIds?: string[];
  onSuccess?: () => void;
  /** Submitting surface, stamped on the batch + runs (Slice 1). */
  source?: AutomationBatchSource;
}

const ACTION_ICONS: Record<
  AutomationActionType,
  React.ComponentType<{ className?: string }>
> = {
  generate_document: FileText,
  send_sign_request: FileSignature,
  send_email: Mail,
  create_event: Calendar,
  create_invoice: CreditCard,
  create_quote: FileSpreadsheet,
  create_support_ticket: Ticket,
  create_internal_task: CheckSquare,
  tag_application: Tag,
  change_workflow_stage: GitCommit,
  update_application_status: RefreshCw,
  add_to_batch: FolderPlus,
  update_batch: FolderPlus,
  update_admin: ShieldCheck,
};

export const AssignAutomationModal: React.FC<AssignAutomationModalProps> = ({
  isOpen,
  onClose,
  referenceApplicant,
  applicantIds = [],
  onSuccess,
  source = 'api',
}) => {
  const [automations, setAutomations] = useState<AutomationDefinition[]>([]);
  // Drafts the workspace HAS but cannot assign yet — counted from the
  // same list load so the empty state can say "drafts exist, publish
  // one" instead of implying no automations exist at all.
  const [draftCount, setDraftCount] = useState(0);
  const [selectedAutoId, setSelectedAutoId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);
  // A failed list load must be VISIBLE: this modal used to load with a
  // bare unauthenticated request (no auth header), the server answered
  // 401, and the swallowed failure rendered identically to "no published
  // automations" — published flows looked unsaved. safeFetch attaches
  // the token + workspace header and throws with the server's message.
  const [loadError, setLoadError] = useState<string | null>(null);
  // Assign outcomes are read from the server's response and shown
  // inline. The pre-fix handler awaited the POST, discarded the body
  // and closed as success — while the server could answer 200 with
  // count 0 (every applicant ID silently skipped), so a no-op looked
  // identical to a real assignment.
  const [assignError, setAssignError] = useState<string | null>(null);
  const [assignResult, setAssignResult] = useState<{
    started: number;
    created: number;
    reused: number;
    notFound: number;
    foreign: number;
    terminal: number;
  } | null>(null);
  // Idempotency token for THIS confirmation session (Slice 3): minted
  // lazily at the first confirm click and reused by every retry of
  // the same confirmation, so a double-click or a network retry can
  // never create a second batch server-side.
  const [submissionToken, setSubmissionToken] = useState<string | null>(null);

  const loadAutomations = async () => {
    setLoading(true);
    try {
      const data = await safeFetch('/api/admin/automations');
      const all: AutomationDefinition[] = data.automations || [];
      const published = all.filter((a) => a.status === 'published');
      setDraftCount(all.filter((a) => a.status === 'draft').length);
      setAutomations(published);
      setLoadError(null);
      if (published.length > 0) {
        setSelectedAutoId(published[0].id);
      }
    } catch (err: any) {
      setAutomations([]);
      setDraftCount(0);
      setSelectedAutoId('');
      setLoadError(err.message || 'Failed to load automations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setAssignError(null);
      setAssignResult(null);
      // A fresh modal opening is a NEW submission: any token from a
      // previous opening is discarded (Slice 3). Retries WITHIN this
      // opening reuse the token minted at the first confirm click.
      setSubmissionToken(null);
      loadAutomations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  const selectedAutomation = automations.find((a) => a.id === selectedAutoId);

  // Execution order for the sequence preview below. The stored nodes
  // array is in AUTHORING order (the canvas keeps it that way), not
  // execution order — rendering it raw showed the "Configured Execution
  // Sequence" scrambled relative to both the canvas badges and what the
  // engine actually runs. Order by computeNodeSequenceMap instead: the
  // single sequencing source the canvas uses (Start-first BFS over the
  // stored edges; the engine's buildExecutionSequence runs the identical
  // walk). Its fallback numbers every unreached node too, so the map
  // covers the whole list; a node with no number (none in practice)
  // keeps its relative stored order after the numbered ones.
  const selectedNodes = selectedAutomation?.nodes || [];
  const selectedSequenceMap = computeNodeSequenceMap(
    selectedNodes,
    selectedAutomation?.edges || []
  );
  const orderedSequenceNodes = selectedNodes
    .map((node, arrayIndex) => ({ node, arrayIndex, seq: selectedSequenceMap.get(node.id) }))
    .sort(
      (a, b) =>
        (a.seq ?? Number.MAX_SAFE_INTEGER) - (b.seq ?? Number.MAX_SAFE_INTEGER) ||
        a.arrayIndex - b.arrayIndex
    );

  const targetApplicantIds = applicantIds.length > 0 ? applicantIds : referenceApplicant?.id ? [referenceApplicant.id] : [];

  const displayName =
    referenceApplicant?.applicantName ||
    referenceApplicant?.fullName ||
    'Selected Applicant';
  const customId =
    referenceApplicant?.customApplicationId ||
    referenceApplicant?.applicationId ||
    referenceApplicant?.id;
  const progName = referenceApplicant?.programName || referenceApplicant?.program || 'Standard Program';
  const stageName = referenceApplicant?.currentStage || referenceApplicant?.stage || 'Draft';

  const handleAssign = async () => {
    // Re-entrancy guard: a second click while a request is in flight
    // must not fire a second request (the server token is the second
    // line of defence).
    if (assigning) return;
    if (!selectedAutoId) {
      setAssignError('Please select a published automation flow.');
      return;
    }
    if (targetApplicantIds.length === 0) {
      setAssignError('No valid reference applicant selected.');
      return;
    }

    // Mint the idempotency token ONCE per confirmation session and
    // reuse it across retries of this same confirmation.
    const token =
      submissionToken ||
      (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `sub-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`);
    if (!submissionToken) setSubmissionToken(token);

    setAssigning(true);
    setAssignError(null);
    setAssignResult(null);
    try {
      const data = await safeFetch('/api/admin/automations/assign', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          automationId: selectedAutoId,
          applicantIds: targetApplicantIds,
          source,
          submissionToken: token,
        }),
      });

      // Read what the server ACTUALLY did. count covers created +
      // reused runs; outcomes carry the per-applicant detail. Only a
      // response that started at least one run is a success — the
      // server now also answers 422 (thrown by safeFetch below) when
      // nothing was started.
      const outcomes: any[] = Array.isArray(data?.outcomes) ? data.outcomes : [];
      const started = typeof data?.count === 'number'
        ? data.count
        : outcomes.filter((o) => o.status === 'created' || o.status === 'reused').length;
      if (!started) {
        setAssignError(
          data?.error || 'The server reported that no automation runs were started.'
        );
        return;
      }
      setAssignResult({
        started,
        created: typeof data?.created === 'number'
          ? data.created
          : outcomes.filter((o) => o.status === 'created').length,
        reused: typeof data?.reused === 'number'
          ? data.reused
          : outcomes.filter((o) => o.status === 'reused').length,
        notFound: outcomes.filter((o) => o.status === 'not_found').length,
        foreign: outcomes.filter((o) => o.status === 'foreign_workspace').length,
        terminal: outcomes.filter((o) => o.status === 'terminal').length,
      });
      if (onSuccess) onSuccess();
    } catch (err: any) {
      setAssignError(err.message || 'Failed to assign automation.');
    } finally {
      setAssigning(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9500] flex items-center justify-center bg-slate-900/60 backdrop-blur-xs p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden animate-scale-in">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50 shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="p-2 rounded-xl bg-brand-primary text-white shadow-sm">
              <Sparkles size={18} />
            </div>
            <div>
              <h3 className="text-base font-extrabold text-slate-900">Assign Automation Flow</h3>
              <p className="text-xs text-slate-500">Execute automated sequence against applicant record.</p>
            </div>
          </div>
          <button aria-label="Close" title="Close"
            onClick={onClose}
            disabled={assigning}
            className="p-2 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-200/50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <X size={18} />
          </button>
        </div>

        {/* Content Body */}
        <div className="p-6 overflow-y-auto space-y-6 flex-1">
          {/* List load failure — distinct from a genuinely empty list */}
          {loadError && (
            <div className="flex items-center justify-between gap-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-xs font-semibold text-rose-700">
              <span className="flex items-center gap-2">
                <AlertTriangle size={14} />
                Couldn't load automations — {loadError}
              </span>
              <button
                type="button"
                onClick={loadAutomations}
                className="px-3 py-1.5 rounded-lg bg-white border border-rose-200 text-rose-700 font-bold hover:bg-rose-100 transition-colors cursor-pointer shrink-0"
              >
                Retry
              </button>
            </div>
          )}

          {/* Reference Applicant Confirmation Card */}
          <div className="p-4 rounded-xl bg-indigo-50/60 border border-indigo-100 space-y-2">
            <span className="text-[10px] font-black uppercase tracking-wider text-indigo-600 block">
              Reference Applicant Context
            </span>
            <div className="flex items-center justify-between gap-4">
              <div>
                <h4 className="text-sm font-extrabold text-slate-900">{displayName}</h4>
                <p className="text-xs text-slate-600">
                  ID: <strong className="font-mono text-indigo-700">{customId}</strong> · Program:{' '}
                  <strong className="text-slate-800">{progName}</strong>
                </p>
              </div>
              <span className="px-3 py-1 rounded-full text-[10px] font-extrabold bg-white text-indigo-700 border border-indigo-200 uppercase">
                {stageName}
              </span>
            </div>
            {applicantIds.length > 1 && (
              <p className="text-[11px] font-semibold text-indigo-800">
                This flow will run for all {applicantIds.length} selected applicants — the applicant
                above is the first of them.
              </p>
            )}
          </div>

          {/* Select Automation Flow */}
          <div>
            <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">
              Select Published Automation
            </label>
            <SearchableSelect
              value={selectedAutoId}
              onChange={(v) => setSelectedAutoId(v)}
              options={automations.map((a) => ({
                value: a.id,
                label: `${a.automationId} — ${a.name} (v${a.currentVersion})`,
              }))}
              placeholder="Search automations…"
              allowClear={false}
              emptyLabel={
                loadError
                  ? "Couldn't load automations"
                  : loading
                    ? 'Loading automations…'
                    : draftCount > 0
                      ? `No published automations available — ${draftCount} draft(s) exist; publish one to assign it`
                      : 'No published automations available'
              }
            />
            {automations.length === 0 && !loadError && !loading && draftCount > 0 && (
              <p className="text-[11px] text-slate-500 mt-1.5 font-medium">
                Only published automations can be assigned. Publish one of the {draftCount} draft(s) from
                the Automations page, then reopen this dialog.
              </p>
            )}
          </div>

          {/* Action Sequence Preview */}
          {selectedAutomation && (
            <div className="space-y-3">
              <span className="text-xs font-bold text-slate-700 uppercase tracking-wider block">
                Configured Execution Sequence ({selectedAutomation.nodes?.length || 0} Steps)
              </span>

              <div className="space-y-2 max-h-60 overflow-y-auto p-1">
                {orderedSequenceNodes.map(({ node, seq }, idx) => {
                  const Icon = ACTION_ICONS[node.data.actionType] || FileText;
                  const delayMin = (node.data.delayValue || 0) * (node.data.delayUnit === 'hours' ? 60 : node.data.delayUnit === 'days' ? 1440 : 1);

                  return (
                    <div
                      key={node.id}
                      className="p-3 rounded-xl border border-slate-200/80 bg-slate-50 flex items-center justify-between gap-3 text-xs"
                    >
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-white border border-slate-200 text-indigo-600">
                          <Icon className="h-4 w-4" />
                        </div>
                        <div>
                          <span className="text-[10px] font-black text-slate-400 uppercase block">
                            Step {seq ?? idx + 1}
                          </span>
                          <strong className="font-extrabold text-slate-800">{node.data.label}</strong>
                        </div>
                      </div>

                      <div className="text-right">
                        <span className="text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-md flex items-center gap-1">
                          <Clock size={10} />
                          {delayMin === 0 ? 'Immediate' : `+${node.data.delayValue} ${node.data.delayUnit}`}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Assign outcome — pinned above the footer so it is always
            visible: what the server actually started, or why not. */}
        {assignError && (
          <div className="px-6 py-3 border-t border-rose-100 bg-rose-50 text-xs font-semibold text-rose-700 flex items-center gap-2 shrink-0">
            <AlertTriangle size={14} className="shrink-0" />
            <span>{assignError}</span>
          </div>
        )}
        {assignResult && (
          <div
            className={`px-6 py-3 border-t text-xs font-semibold flex items-center gap-2 shrink-0 ${
              assignResult.notFound > 0 || assignResult.foreign > 0 || assignResult.terminal > 0
                ? 'border-amber-100 bg-amber-50 text-amber-800'
                : 'border-emerald-100 bg-emerald-50 text-emerald-700'
            }`}
          >
            <CheckCircle2 size={14} className="shrink-0" />
            <span>
              {assignResult.started} automation run{assignResult.started === 1 ? '' : 's'} started
              {assignResult.reused > 0 && assignResult.created > 0
                ? ` (${assignResult.created} new, ${assignResult.reused} already active)`
                : assignResult.reused > 0
                  ? ' (already active — no duplicates created)'
                  : ''}
              {assignResult.notFound > 0
                ? ` · ${assignResult.notFound} selected applicant(s) couldn't be matched to an application`
                : ''}
              {assignResult.foreign > 0
                ? ` · ${assignResult.foreign} selected applicant(s) belong to a different workspace and were skipped`
                : ''}
              {assignResult.terminal > 0
                ? ` · ${assignResult.terminal} selected applicant(s) are in a terminal status (cancelled, withdrawn or archived) and were blocked`
                : ''}
            </span>
          </div>
        )}

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-end gap-3 bg-slate-50 shrink-0">
          <button
            onClick={onClose}
            disabled={assigning}
            className="px-4 py-2 rounded-xl text-xs font-bold text-slate-600 hover:bg-slate-200/60 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          {assignResult ? (
            <button
              onClick={onClose}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-xs font-extrabold text-white bg-success hover:bg-[color-mix(in_srgb,var(--color-success)_90%,#000)] transition-colors shadow-md shadow-success/20 cursor-pointer"
            >
              <CheckCircle2 size={14} />
              Done
            </button>
          ) : (
            <button
              onClick={handleAssign}
              disabled={assigning || !selectedAutoId}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-xs font-extrabold text-white bg-brand-primary hover:bg-brand-primary-dark disabled:opacity-50 transition-colors shadow-md shadow-brand-primary/20 cursor-pointer"
            >
              <Play size={14} className="fill-white" />
              {assigning ? 'Assigning Flow...' : 'Confirm & Execute Automation'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
